import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Check, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

// 16 Pantone-standard swatches matching the provided palette image
export const PANTONE_SWATCHES = [
  { code: 'P-186',  name: 'Red 186 C',        hex: '#E4002B' },
  { code: 'P-012',  name: 'Yellow 012 C',     hex: '#FFD100' },
  { code: 'P-7683', name: 'Slate Blue 7683',  hex: '#526DB0' },
  { code: 'P-3275', name: 'Teal 3275 C',      hex: '#00A693' },
  { code: 'P-REF',  name: 'Reflex Blue C',    hex: '#002677' },
  { code: 'P-2582', name: 'Purple 2582 C',    hex: '#8347AD' },
  { code: 'P-226',  name: 'Magenta 226 C',    hex: '#CE0F69' },
  { code: 'P-340',  name: 'Emerald 340 C',    hex: '#00945D' },
  { code: 'P-YEL',  name: 'Pantone Yellow',   hex: '#FEDD00' },
  { code: 'P-123',  name: 'Golden 123 C',     hex: '#FFC72C' },
  { code: 'P-2718', name: 'Slate 2718 C',     hex: '#5B6D99' },
  { code: 'P-RUB',  name: 'Rubine Red',       hex: '#E10098' },
  { code: 'P-354',  name: 'Green 354 C',      hex: '#00B140' },
  { code: 'P-191',  name: 'Rose 191 C',       hex: '#EA638F' },
  { code: 'P-021',  name: 'Orange 021 C',     hex: '#FF671F' },
  { code: 'P-298',  name: 'Sky Blue 298 C',   hex: '#41B6E6' },
  { code: 'P-281',  name: 'Navy 281 C',       hex: '#00205B' },
  { code: 'P-368',  name: 'Lime 368 C',       hex: '#78BE20' },
];

// HEX -> HSL (space-separated for CSS custom property)
export function hexToHslTriplet(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      case b: hh = (r - g) / d + 4; break;
      default: break;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const ROLES = [
  { key: 'primary',     label: 'Primary',     defaultHex: '#002677' },
  { key: 'secondary',   label: 'Secondary',   defaultHex: '#FFD100' },
  { key: 'accent',      label: 'Accent',      defaultHex: '#41B6E6' },
  { key: 'success',     label: 'Success',     defaultHex: '#00B140' },
  { key: 'destructive', label: 'Destructive', defaultHex: '#E4002B' },
];

const STORAGE_KEY = 'pantone-palette-mapping';

export function applyPantonePalette(mapping) {
  const root = document.documentElement;
  ROLES.forEach((r) => {
    const hex = mapping?.[r.key] || r.defaultHex;
    const hsl = hexToHslTriplet(hex);
    root.style.setProperty(`--${r.key}`, hsl);
    // Keep matching ring for primary
    if (r.key === 'primary') root.style.setProperty('--ring', hsl);
  });
}

export function loadPantonePalette() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function PantonePalettePicker() {
  const stored = loadPantonePalette() || {};
  const [mapping, setMapping] = useState({
    primary:     stored.primary     || '#002677',
    secondary:   stored.secondary   || '#FFD100',
    accent:      stored.accent      || '#41B6E6',
    success:     stored.success     || '#00B140',
    destructive: stored.destructive || '#E4002B',
  });
  const [activeRole, setActiveRole] = useState('primary');

  // Apply live preview whenever mapping changes
  useEffect(() => { applyPantonePalette(mapping); }, [mapping]);

  const assign = (hex) => {
    setMapping((prev) => ({ ...prev, [activeRole]: hex }));
  };

  const save = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mapping));
    applyPantonePalette(mapping);
    toast.success('Pantone palette saved for this workspace');
  };

  const reset = () => {
    const def = ROLES.reduce((acc, r) => ({ ...acc, [r.key]: r.defaultHex }), {});
    setMapping(def);
    localStorage.removeItem(STORAGE_KEY);
    applyPantonePalette(def);
    toast.success('Palette reset to Pantone defaults');
  };

  return (
    <div className="bg-card border border-border p-6 rounded-sm" data-testid="pantone-picker">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold">Pantone Palette</h2>
          <p className="text-sm text-muted-foreground">
            Pick a role, then click any swatch to assign. Changes preview instantly across the app.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reset} data-testid="pantone-reset">
            <ArrowCounterClockwise size={14} className="mr-1.5" />
            Reset
          </Button>
          <Button size="sm" onClick={save} data-testid="pantone-save">
            <Check size={14} className="mr-1.5" />
            Save palette
          </Button>
        </div>
      </div>

      {/* Role selector */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="pantone-roles">
        {ROLES.map((r) => (
          <button
            key={r.key}
            onClick={() => setActiveRole(r.key)}
            data-testid={`pantone-role-${r.key}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-sm transition-all ${
              activeRole === r.key
                ? 'border-foreground bg-foreground/5 shadow-sm'
                : 'border-border hover:border-foreground/40'
            }`}
          >
            <span
              className="h-4 w-4 rounded-sm border border-black/10"
              style={{ backgroundColor: mapping[r.key] }}
            />
            <span className="font-medium">{r.label}</span>
            <span className="text-xs text-muted-foreground font-mono">{mapping[r.key]}</span>
          </button>
        ))}
      </div>

      {/* 16-swatch grid (4 columns like the reference) */}
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-9 gap-3" data-testid="pantone-swatches">
        {PANTONE_SWATCHES.map((sw) => {
          const isActive = mapping[activeRole]?.toUpperCase() === sw.hex.toUpperCase();
          return (
            <button
              key={sw.code}
              type="button"
              onClick={() => assign(sw.hex)}
              data-testid={`pantone-swatch-${sw.code}`}
              className="group relative aspect-[4/3] rounded-sm overflow-hidden border border-black/5 hover:scale-[1.04] transition-transform focus:outline-none focus:ring-2 focus:ring-foreground"
              style={{ backgroundColor: sw.hex }}
              title={`${sw.name} (${sw.hex})`}
            >
              {isActive && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Check size={20} weight="bold" className="text-white drop-shadow" />
                </span>
              )}
              <span className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[9px] font-mono bg-black/40 text-white truncate">
                {sw.code}
              </span>
            </button>
          );
        })}
      </div>

      {/* Live preview strip */}
      <div className="mt-6 border-t border-border pt-4">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">
          Live preview
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="h-9 px-4 rounded-sm text-sm font-medium flex items-center bg-primary text-primary-foreground">Primary button</div>
          <div className="h-9 px-4 rounded-sm text-sm font-medium flex items-center bg-secondary text-secondary-foreground">Secondary</div>
          <div className="h-9 px-4 rounded-sm text-sm font-medium flex items-center bg-accent text-accent-foreground">Accent chip</div>
          <div className="h-9 px-4 rounded-sm text-sm font-medium flex items-center bg-success text-success-foreground">Success</div>
          <div className="h-9 px-4 rounded-sm text-sm font-medium flex items-center bg-destructive text-destructive-foreground">Destructive</div>
        </div>
      </div>
    </div>
  );
}
